// The Wi-Fi light's On / Off / Night / Blink row and its ⋮ items (Blink to
// identify, Reverse white / warm white) — the same set the BLE lamp block has
// (ui/shared/ble/BleNodePanel ControlBlock / LampRow), for DeviceIDs 23, 24, 25
// and 90. Drawn by the phone LightScreen, the desktop light page and the desktop
// room card, all of which run on core/hooks/useLightChannels.
//
// There are no groups here: every Wi-Fi light is its own device.
//
// Reverse is owner / Super Admin only, as on the BLE lamps, and only on a light
// with a white pair (wwh, wwrgb) — RGB has nothing to swap.

import React, { useEffect, useState } from 'react';
import { Box, Button, Snackbar } from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useApp } from '../../../core/state/AppContext';
import {
  cachedOwner, getLightReversed, onReverseChange, setLightReversed,
} from '../../../core/devices/lightReverse';

/** On / Off / Night / Blink. `light` is the useLightChannels result. */
export function LightActionRow({ light, color, size = 'medium', sx }) {
  const busy = light.blinking;
  const btn = {
    variant: 'outlined',
    size,
    disabled: busy,
    sx: color ? { color, borderColor: `${color}66` } : undefined,
  };
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', ...sx }}>
      <Button {...btn} onClick={light.turnOn}>On</Button>
      <Button {...btn} onClick={light.turnOff}>Off</Button>
      <Button {...btn} onClick={light.nightMode}>Night</Button>
      <Button {...btn} onClick={light.blink} startIcon={<FlashOnIcon />}>
        {busy ? 'Blinking…' : 'Blink'}
      </Button>
    </Box>
  );
}

/**
 * The ⋮ entries for a Wi-Fi light, in a neutral shape
 * `{ key, label, secondary, icon, onClick }` — each menu maps it to its own.
 * -> { items, notice, clearNotice }
 */
export function useLightMenu(device, light) {
  const { isSuperAdmin, realUserId, userId } = useApp();
  const uno = device?.UniqueNumber;
  const [notice, setNotice] = useState('');
  const [owner, setOwner] = useState(() => device?.MasterUserId ?? cachedOwner(uno));

  // The owner rides along with the install row the reverse flag is read from.
  useEffect(() => {
    if (!uno || !light.canReverse) return undefined;
    let on = true;
    getLightReversed(device).then(() => { if (on && owner == null) setOwner(cachedOwner(uno)); });
    const off = onReverseChange((u) => { if (on && String(u) === String(uno) && owner == null) setOwner(cachedOwner(uno)); });
    return () => { on = false; off(); };
  }, [uno, light.canReverse]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOwner = isSuperAdmin || (owner != null && String(owner) === String(realUserId));

  const items = [{
    key: 'blink',
    label: 'Blink to identify',
    icon: <FlashOnIcon fontSize="small" />,
    disabled: light.blinking,
    onClick: () => light.blink(),
  }];
  if (light.canReverse && isOwner) {
    items.push({
      key: 'reverse',
      label: light.reversed ? 'Reversed white / warm ✓' : 'Reverse white / warm white',
      secondary: light.reversed ? 'Tap to set back to normal' : 'If White shows warm on this light',
      icon: <SwapHorizIcon fontSize="small" />,
      // ⚠️ SAVED IN TWO PLACES AND ONLY ONE OF THEM IS CERTAIN. The column
      // always takes it; the device takes it now only if it is powered up and
      // online, and until it does the app goes on exchanging the two channels
      // itself — so either way the light is right, and the message says which
      // happened rather than claiming a save nothing acknowledged.
      // ⚠️ ONE SHORT LINE (owner, 2026-10-26) — it is a toast, not a page.
      onClick: async () => {
        const next = !light.reversed;
        setNotice(next ? 'Reversing…' : 'Setting back to normal…');
        const r = await setLightReversed(uno, next, userId);
        if (!r.ok) { setNotice(r.message); return; }
        setNotice(next ? 'Reversed — White now shows white.' : 'Back to normal.');
      },
    });
  }
  return { items, notice, clearNotice: () => setNotice('') };
}

/** The one-line answer after Reverse. */
export function LightNotice({ notice, onClose }) {
  return (
    <Snackbar
      open={!!notice}
      autoHideDuration={4000}
      onClose={onClose}
      message={notice}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );
}
